import React, { useState, useEffect } from 'react';
import { ProcessYield } from '@twinsketch/topika-model';
import 'src/styles/workOrder/work-order.css';
import 'src/styles/workOrder/mixing-table.css';
import { FiCheck, FiEdit2, FiX } from 'react-icons/fi';

interface ProcessYieldTableProps {
  processYield: ProcessYield | null | undefined;
  onUpdate: (data: ProcessYield) => Promise<void>;
  readonly?: boolean;
}

const NUMERIC_FIELDS: (keyof ProcessYield)[] = [
  'mixing',
  'coating',
  'calendering',
  'slitting',
  'total',
];

const DEFAULT_YIELDS = {
  mixing: 95,
  coating: 85,
  calendering: 95,
  slitting: 0,
};

const formatNumber = (value: number | null | undefined) => {
  return typeof value === 'number' ? value.toFixed(2) : 'N/A';
};

const calculateTotalYield = (data: ProcessYield): number => {
  const mixing = data.mixing ?? 0;
  const coating = data.coating ?? 0;
  const calendering = data.calendering ?? 0;
  const slitting = data.slitting ?? 0;

  // Total yield is the product of all individual yields divided by 100^3 (since we're multiplying percentages)
  // Formula: (mixing/100) * (coating/100) * (calendering/100) * (slitting/100) * 100
  // Or simplified: (mixing * coating * calendering * slitting) / 1000000

  if (slitting === 0) {
    // If slitting is 0, calculate without it
    return (mixing * coating * calendering) / 10000;
  }

  return (mixing * coating * calendering * slitting) / 1000000;
};

const ProcessYieldTable: React.FC<ProcessYieldTableProps> = ({
  processYield,
  onUpdate,
  readonly = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<ProcessYield>(createInitialFormData(processYield));

  useEffect(() => {
    if (processYield) {
      setFormData(createInitialFormData(processYield));
    }
  }, [processYield]);

  // Recalculate total whenever formData changes
  useEffect(() => {
    if (isEditing) {
      const total = calculateTotalYield(formData);
      setFormData(prev => ({ ...prev, total }));
    }
  }, [formData.mixing, formData.coating, formData.calendering, formData.slitting, isEditing]);

  const handleChange = (field: keyof ProcessYield, value: string) => {
    const numValue = value === '' ? undefined : Number(value);
    setFormData(prev => ({ ...prev, [field]: numValue }));
  };

  const handleEditClick = () => {
    // Apply default values for any field that is 0, null, or undefined
    const dataWithDefaults: ProcessYield = {
      mixing:
        !processYield?.mixing || processYield.mixing === 0
          ? DEFAULT_YIELDS.mixing
          : processYield.mixing,
      coating:
        !processYield?.coating || processYield.coating === 0
          ? DEFAULT_YIELDS.coating
          : processYield.coating,
      calendering:
        !processYield?.calendering || processYield.calendering === 0
          ? DEFAULT_YIELDS.calendering
          : processYield.calendering,
      slitting:
        !processYield?.slitting || processYield.slitting === 0
          ? DEFAULT_YIELDS.slitting
          : processYield.slitting,
      total: processYield?.total,
    };

    setFormData(dataWithDefaults);
    setIsEditing(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await onUpdate(formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save process yield:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData(createInitialFormData(processYield));
    setIsEditing(false);
  };

  const canEdit = !readonly && !isEditing;

  // if (!processYield && !isEditing) {
  //   return null;
  // }

  return (
    <>
      <div className="formulation-title-row">
        <span className="formulation-title">Material Amount</span>

        {canEdit && (
          <div className="action-buttons" style={{ marginBottom: '8px' }}>
            <button
              className="formulation-link"
              onClick={() => setIsEditing(true)}
              title="Edit"
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                padding: '4px',
              }}
            >
              <FiEdit2 size={16} color="#666" />
            </button>
          </div>
        )}

        {isEditing && (
          <div className="action-buttons">
            <button
              className="formulation-link"
              onClick={handleSave}
              disabled={loading}
              title="Save"
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                padding: '4px',
                marginRight: '4px',
              }}
            >
              <FiCheck size={16} color="#28a745" />
            </button>
            <button
              className="formulation-link"
              onClick={handleCancel}
              disabled={loading}
              title="Cancel"
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                padding: '4px',
              }}
            >
              <FiX size={16} color="#dc3545" />
            </button>
          </div>
        )}
      </div>
      <table className="mixing-table">
        <thead>
          <tr>
            <th>Process</th>
            <th>Yield (%)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Mixing</td>
            <td>
              {isEditing ? (
                <input
                  type="number"
                  className="table-input-small"
                  placeholder="0.00"
                  value={formData.mixing ?? ''}
                  onChange={e => handleChange('mixing', e.target.value)}
                  step="0.01"
                  min="0"
                  max="100"
                />
              ) : (
                formatNumber(processYield?.mixing)
              )}
            </td>
          </tr>
          <tr>
            <td>Coating</td>
            <td>
              {isEditing ? (
                <input
                  type="number"
                  className="table-input-small"
                  placeholder="0.00"
                  value={formData.coating ?? ''}
                  onChange={e => handleChange('coating', e.target.value)}
                  step="0.01"
                  min="0"
                  max="100"
                />
              ) : (
                formatNumber(processYield?.coating)
              )}
            </td>
          </tr>
          <tr>
            <td>Calendering</td>
            <td>
              {isEditing ? (
                <input
                  type="number"
                  className="table-input-small"
                  placeholder="0.00"
                  value={formData.calendering ?? ''}
                  onChange={e => handleChange('calendering', e.target.value)}
                  step="0.01"
                  min="0"
                  max="100"
                />
              ) : (
                formatNumber(processYield?.calendering)
              )}
            </td>
          </tr>
          <tr>
            <td>Slitting</td>
            <td>
              {isEditing ? (
                <input
                  type="number"
                  className="table-input-small"
                  placeholder="0.00"
                  value={formData.slitting ?? ''}
                  onChange={e => handleChange('slitting', e.target.value)}
                  step="0.01"
                  min="0"
                  max="100"
                />
              ) : (
                formatNumber(processYield?.slitting)
              )}
            </td>
          </tr>
          <tr>
            <td>Total</td>
            <td>
              <strong>{formatNumber(isEditing ? formData.total : processYield?.total)}</strong>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

// ========== Helper Functions ==========

function createInitialFormData(processYield: ProcessYield | null | undefined): ProcessYield {
  return {
    mixing: processYield?.mixing,
    coating: processYield?.coating,
    calendering: processYield?.calendering,
    slitting: processYield?.slitting,
    total: processYield?.total,
  };
}

export default ProcessYieldTable;
